// src/app/error-classify.ts
// Router-free error inspection, shared by the two error wrappers. It lives apart from both
// because `app-error-boundary.tsx` sits ABOVE `<RouterProvider>` and must not pull in router
// hooks, while `route-error-element.tsx` sits inside it and does.

/**
 * Stale-chunk detection. A deploy replaces the hashed bundles under a client that still holds
 * the old manifest, so its next dynamic import 404s — the classic "white screen after a
 * deploy". The PA lazy-loads two encounter panels (`encounter-shell-page.tsx:199,208`), so
 * this is reachable today, and it is the most common production render failure in a Vite SPA.
 *
 * Message-shape matching is the only signal available: the thrown value is a plain `TypeError`
 * from the module loader, carrying no code to key on. The five patterns cover Vite, Rollup's
 * preload helper and the browser-native module loader across Chrome, Firefox and Safari.
 *
 * Mirrors `dev-scrips-pm-react/src/shared/lib/lazy-with-retry.ts`'s detector; kept local
 * rather than shared because the two apps have no common package.
 */
export function isChunkLoadError(err: unknown): boolean {
  const message =
    err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : '';
  return (
    /Loading chunk \d+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/**
 * Human-readable, PHI-free text for the dev-only `<details>` block. Callers gate the CALL on
 * `import.meta.env.DEV` — this function makes no such judgement, so it stays testable.
 */
export function formatErrorDetails(err: unknown): string {
  if (err instanceof Error) return err.stack ?? `${err.name}: ${err.message}`;
  return String(err);
}
